import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTooth, 
  faTeeth, 
  faMagic, 
  faXRay, 
  faTasks, 
  faHeart,
  faSpa,
  faPhone
} from '@fortawesome/free-solid-svg-icons';

import Hero from '../components/Hero';
import ServiceCard from '../components/ServiceCard';
import Gallery from '../components/Gallery';
import logo from '../media/logo_dentiste.png';
import white_logo from '../media/logo_white.png';
import chaise_dentiste from '../media/chaise_dentiste.jpg'

const Accueil: React.FC = () => {
  const services = [
    {
      title: 'Soins',
      description: 'Traitements des caries, dévitalisations, détartrage et autres soins dentaires courants.',
      icon: faTooth,
      link: '/services#soins'
    },
    {
      title: 'Chirurgie',
      description: 'Extractions dentaires, dents de sagesse et autres interventions chirurgicales.',
      icon: faTeeth,
      link: '/services#chirurgie'
    },
    {
      title: 'Dentisterie esthétique',
      description: 'Blanchiment, facettes, couronnes et autres traitements pour un sourire éclatant.',
      icon: faMagic,
      link: '/services#dentisterie-esthetique'
    },
    {
      title: 'Radiographie',
      description: 'Équipement moderne pour un diagnostic précis et des soins adaptés.',
      icon: faXRay,
      link: '/services#radiographie'
    },
    {
      title: 'Implantologie',
      description: 'Remplacement durable et naturel des dents manquantes par des implants dentaires.',
      icon: faTasks,
      link: '/services#implantologie'
    },
    {
      title: 'Parodontologie',
      description: 'Traitement des maladies des gencives pour une meilleure santé bucco-dentaire.',
      icon: faHeart,
      link: '/services#parodontologie'
    },
    {
      title: 'Orthodontie',
      description: 'Alignement des dents pour un sourire harmonieux et une meilleure fonctionnalité.',
      icon: faSpa,
      link: '/services#orthodontie'
    }
  ];

  return (
    <div className="overflow-hidden">
      <Hero />
      <Gallery />
      
      {/* Section Services */}
      <section className="w-full pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="section-title text-accent">Nos Services</h2>
            <p className="section-subtitle">
              Nous offrons une gamme complète de soins dentaires pour toute la famille,
              dans un environnement confortable et avec des technologies modernes.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 6).map((service, index) => (
              <div key={index} className={`animate-fade-in`} style={{animationDelay: `${(index + 1) * 150}ms`}}>
                <ServiceCard 
                  title={service.title}
                  description={service.description}
                  icon={service.icon}
                  link={service.link}
                />
              </div>
            ))}
          </div>
          
          <div className="text-center mt-16 animate-fade-in" style={{animationDelay: '900ms'}}>
            <Link to="/services" className="btn-outline inline-block">
              Voir tous nos services
            </Link>
          </div>
        </div>
      </section>
      
      {/* Section Pourquoi nous choisir */}
      <section className="w-full py-16">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-accent text-white rounded-3xl p-12 shadow-elegant">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="section-title text-white">Pourquoi Nous Choisir?</h2>
              <p className="text-xl max-w-3xl mx-auto text-gray-300">
                Le cabinet du Dr. Banelhaq Ayoub s'engage à fournir les meilleurs soins dentaires 
                avec une attention particulière au confort et à la satisfaction du patient.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              <div className="text-center animate-fade-in" style={{animationDelay: '150ms'}}>
                <div className="h-20 w-20 bg-primary rounded-full mx-auto flex items-center justify-center mb-6">
                  <FontAwesomeIcon icon={faTooth} className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Expertise</h3>
                <p className="text-gray-300">
                  Une équipe expérimentée et formée aux dernières techniques dentaires.
                </p>
              </div>
              
              <div className="text-center animate-fade-in" style={{animationDelay: '300ms'}}>
                <div className="h-20 w-20 bg-primary rounded-full mx-auto flex items-center justify-center mb-6">
                  <FontAwesomeIcon icon={faMagic} className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Technologie</h3>
                <p className="text-gray-300">
                  Des équipements modernes pour des diagnostics précis et des traitements efficaces.
                </p>
              </div>
              
              <div className="text-center animate-fade-in" style={{animationDelay: '450ms'}}>
                <div className="h-20 w-20 bg-primary rounded-full mx-auto flex items-center justify-center mb-6">
                  <FontAwesomeIcon icon={faHeart} className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Confort</h3>
                <p className="text-gray-300">
                  Une approche centrée sur le patient pour une expérience dentaire sans stress.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Section À propos */}
      <section className="w-full py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in" style={{animationDelay: '150ms'}}>
              <h2 className="section-title text-accent mb-6">Dr. Banelhaq Ayoub</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Fort d'une solide formation et de plusieurs années d'expérience, le Dr. Banelhaq Ayoub s'engage à fournir des soins dentaires de la plus haute qualité dans une ambiance accueillante et rassurante.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Notre cabinet est équipé des technologies les plus récentes pour garantir des traitements efficaces et confortables. Notre équipe est dévouée à assurer votre bien-être et à réaliser le sourire dont vous avez toujours rêvé.
              </p>
              <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-8">
                <Link to="/contact" className="btn-primary">
                  En savoir plus
                </Link>
                <a 
                  href="tel:+212684707407" 
                  className="btn-outline flex items-center justify-center"
                >
                  <FontAwesomeIcon icon={faPhone} className="mr-2" />
                  +212 684 707 407
                </a>
              </div>
            </div>
            <div className="bg-gray-100 h-[500px] rounded-3xl flex items-center justify-center shadow-elegant overflow-hidden animate-fade-in" style={{animationDelay: '300ms'}}>
              <img 
                src={chaise_dentiste} 
                alt="Dr. Banelhaq Ayoub - Logo" 
                className="max-w-full object-cover object-center"
                style={{ height: '100%', width: '100%', objectPosition: 'center 55%' }}
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Section Contact et Rendez-vous */}
      <section className="w-full py-16 mb-10">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* WhatsApp Card */}

            
            {/* Rendez-vous Card */}
            <div className="bg-gradient-to-r from-primary to-accent text-white rounded-3xl shadow-elegant h-full">
              <div className="p-8 md:p-10 lg:p-12 text-center flex flex-col h-full animate-fade-in">
                <h2 className="text-2xl md:text-3xl font-bold mb-6">Prenez rendez-vous dès aujourd'hui</h2>
                <p className="text-lg md:text-xl mb-8 flex-grow">
                  Nous nous engageons à vous offrir les meilleurs soins dentaires dans un environnement confortable et accueillant.
                </p>
                <div className="mt-auto">
                  <Link to="/rendez-vous" className="bg-white text-primary hover:bg-gray-100 font-medium px-6 py-4 rounded-md transition-colors inline-block">
                    Réserver maintenant
                  </Link>
                </div>
              </div>
            </div>

            <div className="bg-green-600 rounded-3xl shadow-elegant h-full">
              <div className="text-center text-white p-8 md:p-10 lg:p-12 flex flex-col h-full">
                <div className="flex-grow flex flex-col justify-center animate-fade-in">
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">Besoin d'une consultation rapide?</h2>
                  <p className="text-lg md:text-xl mb-8">
                    Contactez-nous directement sur WhatsApp pour une réponse rapide à vos questions.
                  </p>
                  <div className="mt-auto">
                    <a 
                      href="https://wa.me/212684707407" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white text-green-600 hover:bg-gray-100 font-medium px-6 py-4 rounded-md transition-colors inline-flex items-center"
                    >
                      <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      +212 684 707 407
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Accueil; 